import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import InfoIcon from "@mui/icons-material/InfoOutlined";

import { useAuth, type ProviderData } from 'components/Auth';
import { renderEventMatchingIcon } from 'components/Calendaring';
import { type CICase } from 'components/CriticalIncident/interfaces';
import { computeTotalTime } from 'components/CriticalIncident/utils/CaseSummaryUtils';
import { type ColumnDefinition } from 'components/Shared/MaterialTable';
import useTimezoneUtils from 'hooks/useTimezoneUtils';
import { useTimezoneDiffText } from '../../TimezoneDiffAlert';
import { toShortTimezone } from 'utils';
import { COUNTRY_CODE, useProviderCountryCode } from 'utils/regionUtils';
import { IncidentReportStatusChip } from '../../CICaseSummary/CICaseSummaryTabs/IncidentReportTab/IncidentReportStatusChip';
import { REF_TYPES, useGetReferences } from 'api/queries/referencesQuery';

const StartDateAndTime: React.FC<{ ciCase: CICase }> = ({ ciCase }) => {
    const { localeTimeFormat, formatDate } = useTimezoneUtils();

    const { timezoneDiffText } = useTimezoneDiffText({ clientTimezone: ciCase.timezone });

    const formattedDate = formatDate(moment(ciCase.interventionStartDateTime), `MMM Do, ${localeTimeFormat}`);

    return (
        <span>
            {formattedDate}
            {timezoneDiffText && <span className='timezone-diff-tooltip' data-tooltip={timezoneDiffText}><InfoIcon color="primary" /></span>}
        </span>
    );
}

export const useCICaseColumnsDefinitons = (): ColumnDefinition[] => {
    const { t } = useTranslation('criticalIncident', { useSuspense: false });
    const { effectiveProviderData = {} as ProviderData } = useAuth();
    const timezoneSuffix = toShortTimezone(effectiveProviderData?.timeZone ?? '');
    const isAustralianProvider = useProviderCountryCode() === COUNTRY_CODE.AUSTRALIA;
    const { data: presentingIssues } = useGetReferences(REF_TYPES.PRESENTING_ISSUES);

    const renderWorkOrderIDCell = (ciCase: CICase) => (
        <Link to={{ pathname: `/critical_incident/cases/${ciCase.caseId}/activities/${ciCase.activityId}` }}>{`${ciCase.externalWorkOrderId}`}</Link>
    );

    const renderModality = (ciCase: CICase) => (
        <div className="ci_case_table_detail">
            {renderEventMatchingIcon(ciCase.modalityCode)}
            <span>{t(ciCase.modalityCode)}</span>
        </div>
    );

    const renderStartDateAndTime = (ciCase: CICase) => (<StartDateAndTime ciCase={ciCase} />);

    const renderDuration = (ciCase: CICase) =>
        computeTotalTime(t, ciCase.interventionStartDateTime, ciCase.interventionEndDateTime, true);

    const renderReportStatusCell = (ciCase: CICase) => (
        <IncidentReportStatusChip status={ciCase?.incidentReportStatus} href={`/critical_incident/cases/${ciCase.caseId}/activities/${ciCase.activityId}/report`} />
    );

    const caseIdColumn: ColumnDefinition = {
        title: t('case_id'),
        field: 'externalCaseId',
        width: '15%'
    };

    const workOrderIdColumn: ColumnDefinition = {
        title: t('work_order_id'),
        template: renderWorkOrderIDCell,
        width: '17%'
    };

    const presentingIssueColumn: ColumnDefinition = {
        title: t('incident_category'),
        template: (ciCase: CICase) => presentingIssues?.find(issue => issue.value === ciCase.presentingIssueCode)?.title || "...",
        width: '15%'
    };

    return [
        ...(isAustralianProvider ? [caseIdColumn] : []),
        workOrderIdColumn,
        {
            title: t('client'),
            field: 'organizationName',
            width: '15%'
        },
        ...(isAustralianProvider ? [] : [presentingIssueColumn]),
        {
            title: t('modality_or_location'),
            template: renderModality,
            width: '17%'
        },
        {
            title: `${t('date_and_time')} (${timezoneSuffix})`,
            template: renderStartDateAndTime,
            width: '17%'
        },
        {
            title: t('duration'),
            template: renderDuration,
            width: '9%'
        },
        {
            title: t('reporting'),
            template: renderReportStatusCell,
            width: '11%'
        }
    ];
};
